import { useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import MediaDisplay from "@/components/exam/MediaDisplay";
import type { AnswerWithCorrect, ControllerExamDetail, ControllerQuestion, ControllerQuestionUpdate } from "@/types";

interface EditForm {
  text: string;
  explanation: string;
  correct_answer_id: string | undefined;
  image_url: string;
  answers: { id: string; text: string }[];
}

export default function ControllerExamDetail() {
  const { examId } = useParams<{ examId: string }>();
  const queryClient = useQueryClient();
  const [editingQuestion, setEditingQuestion] = useState<ControllerQuestion | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({
    text: "",
    explanation: "",
    correct_answer_id: undefined,
    image_url: "",
    answers: [],
  });
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: exam, isLoading } = useQuery({
    queryKey: ["controller", "exams", examId],
    queryFn: () => api.get<ControllerExamDetail>(`/controller/exams/${examId}`),
    enabled: !!examId,
  });

  const updateQuestion = useMutation({
    mutationFn: ({ questionId, data }: { questionId: string; data: ControllerQuestionUpdate }) =>
      api.patch(`/controller/questions/${questionId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["controller", "exams", examId] });
      setEditingQuestion(null);
      setSaveError(null);
    },
    onError: (err: any) => {
      setSaveError(err?.data?.detail ?? "Failed to save changes.");
    },
  });

  function openEdit(question: ControllerQuestion) {
    const correctAnswer = question.answers.find((a) => a.is_correct);
    setEditForm({
      text: question.text,
      explanation: question.explanation ?? "",
      correct_answer_id: correctAnswer?.id,
      image_url: question.image_url ?? "",
      answers: question.answers
        .slice()
        .sort((a, b) => a.display_order - b.display_order)
        .map((a) => ({ id: a.id, text: a.text })),
    });
    setEditingQuestion(question);
    setSaveError(null);
  }

  function handleSave() {
    if (!editingQuestion) return;
    const payload: ControllerQuestionUpdate = {};

    if (editForm.text !== editingQuestion.text) payload.text = editForm.text;
    if (editForm.explanation !== (editingQuestion.explanation ?? "")) payload.explanation = editForm.explanation;

    const originalCorrect = editingQuestion.answers.find((a) => a.is_correct);
    if (editForm.correct_answer_id !== originalCorrect?.id) {
      payload.correct_answer_id = editForm.correct_answer_id;
    }

    if (editForm.image_url !== (editingQuestion.image_url ?? "")) {
      payload.image_url = editForm.image_url || undefined;
    }

    // Check if any answer texts changed
    const originalAnswerMap = Object.fromEntries(editingQuestion.answers.map((a) => [a.id, a.text]));
    const changedAnswers = editForm.answers.filter((a) => a.text !== originalAnswerMap[a.id]);
    if (changedAnswers.length > 0) {
      payload.answers = changedAnswers;
    }

    updateQuestion.mutate({ questionId: editingQuestion.id, data: payload });
  }

  async function handleImageUpload(file: File) {
    if (!editingQuestion) return;
    setUploading(true);
    setSaveError(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const token = localStorage.getItem("token");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`/api/v1/controller/questions/${editingQuestion.id}/image`, {
        method: "POST",
        headers,
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveError(err?.detail ?? "Image upload failed.");
        return;
      }
      const updated = await res.json();
      setEditForm((f) => ({ ...f, image_url: updated.image_url ?? "" }));
      queryClient.invalidateQueries({ queryKey: ["controller", "exams", examId] });
    } catch {
      setSaveError("Image upload failed.");
    } finally {
      setUploading(false);
    }
  }

  if (isLoading) {
    return <div className="py-12 text-center text-gray-500">Loading exam...</div>;
  }

  if (!exam) {
    return <div className="py-12 text-center text-gray-500">Exam not found.</div>;
  }

  return (
    <div>
      <div className="mb-6">
        <Link to="/controller" className="text-sm text-primary-600 hover:underline">
          &larr; Back to exams
        </Link>
        <h2 className="mt-2 text-2xl font-bold text-gray-900">{exam.title}</h2>
        {exam.description && <p className="mt-1 text-gray-500">{exam.description}</p>}
        <div className="mt-2 flex gap-4 text-sm text-gray-400">
          <span>{exam.total_questions} questions</span>
          <span>{exam.time_limit_minutes} min</span>
          <span>Pass mark: {exam.pass_mark}</span>
        </div>
      </div>

      <div className="space-y-6">
        {exam.questions.map((question, index) => (
          <div
            key={question.id}
            className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">
                  Question {index + 1}
                </div>
                <MediaDisplay
                  imageUrl={question.image_url}
                  videoUrl={question.video_url}
                  hasImage={question.has_image}
                  hasVideo={question.has_video}
                />
                <p className="text-gray-900 font-medium">{question.text}</p>
              </div>
              <button
                onClick={() => openEdit(question)}
                className="shrink-0 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 hover:border-primary-400 hover:text-primary-600 transition-colors"
              >
                Edit
              </button>
            </div>

            <div className="mt-4 space-y-2">
              {question.answers.map((answer: AnswerWithCorrect) => (
                <div
                  key={answer.id}
                  className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${
                    answer.is_correct
                      ? "bg-green-50 border border-green-200 text-green-800"
                      : "bg-gray-50 border border-gray-200 text-gray-700"
                  }`}
                >
                  <span className="mt-0.5 shrink-0">
                    {answer.is_correct ? (
                      <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </span>
                  {answer.text}
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-md bg-blue-50 border border-blue-100 px-3 py-2 text-sm text-blue-800">
              <span className="font-medium">Explanation: </span>
              {question.explanation}
            </div>
          </div>
        ))}
      </div>

      {/* Edit modal */}
      {editingQuestion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h3 className="text-lg font-semibold text-gray-900">Edit Question</h3>
            </div>
            <div className="px-6 py-5 space-y-5 max-h-[70vh] overflow-y-auto">

              {/* Question text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Text
                </label>
                <textarea
                  rows={3}
                  value={editForm.text}
                  onChange={(e) => setEditForm((f) => ({ ...f, text: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Image
                </label>
                {editForm.image_url && (
                  <img
                    src={editForm.image_url}
                    alt="Current"
                    className="mb-2 max-h-32 rounded-lg border border-gray-200 object-contain"
                  />
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Image URL (or upload below)"
                    value={editForm.image_url}
                    onChange={(e) => setEditForm((f) => ({ ...f, image_url: e.target.value }))}
                    className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {uploading ? "Uploading..." : "Upload"}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleImageUpload(file);
                    }}
                  />
                </div>
              </div>

              {/* Answer options */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Answer Options
                </label>
                <div className="space-y-2">
                  {editForm.answers.map((ans, idx) => {
                    const isCorrect = editForm.correct_answer_id === ans.id;
                    return (
                      <div key={ans.id} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="correct_answer"
                          checked={isCorrect}
                          onChange={() => setEditForm((f) => ({ ...f, correct_answer_id: ans.id }))}
                          className="h-4 w-4 accent-green-600 shrink-0"
                          title="Mark as correct"
                        />
                        <input
                          type="text"
                          value={ans.text}
                          onChange={(e) => {
                            const newAnswers = [...editForm.answers];
                            newAnswers[idx] = { ...ans, text: e.target.value };
                            setEditForm((f) => ({ ...f, answers: newAnswers }));
                          }}
                          className={`flex-1 rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                            isCorrect
                              ? "border-green-400 bg-green-50 focus:border-green-500 focus:ring-green-200"
                              : "border-gray-300 focus:border-primary-500 focus:ring-primary-200"
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
                <p className="mt-1 text-xs text-gray-400">Click the radio button to set the correct answer.</p>
              </div>

              {/* Explanation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Explanation
                </label>
                <textarea
                  rows={3}
                  value={editForm.explanation}
                  onChange={(e) => setEditForm((f) => ({ ...f, explanation: e.target.value }))}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              {saveError && (
                <p className="text-sm text-red-600">{saveError}</p>
              )}
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button
                onClick={() => setEditingQuestion(null)}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateQuestion.isPending}
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
              >
                {updateQuestion.isPending ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
